/**
 * Which tab this shell opens, and which tabs it is allowed to open at all.
 *
 * Pure and React-free on purpose. The rule it encodes is the one the hub keeps
 * getting wrong in the same way: hiding a button is not the same as closing a
 * door, because `?tab=billing` is a URL anybody can type. So the menu and the
 * URL guard must ask ONE question, and this file is where that question lives.
 * `visibleAccountTabs` draws the menu; `resolveActiveTab` decides what renders.
 * Feed them the same context and they cannot disagree.
 *
 * ── THE THREE AUDIENCES ────────────────────────────────────────────────────
 *
 * 1. Husly's own hub (`account.husly.app`): every tab.
 * 2. The operator's client portal (`portal.webadmin.ee`): the same, minus the
 *    tabs that are about Husly the product rather than about the person reading
 *    them. A client who came to look at an invoice has no Husly plan to upgrade.
 * 3. A staff preview: the portal's menu exactly as the client sees it, because
 *    that is the whole promise of "view as customer". The account SCREENS stay
 *    behind a notice (see PreviewAccountNotice) - the token names a client, not
 *    a person, and the data behind those screens would be the OPERATOR's own.
 */

export type TabKey =
  | "overview" | "profile" | "security" | "notifications" | "preferences" | "privacy" | "billing" | "usage"
  | "referral" | "apikeys" | "invoices" | "estimates" | "statement" | "projects" | "support" | "contracts";

/**
 * Tabs that belong to Husly the product, not to the person reading them.
 *
 * Hidden on the operator's portal and in a preview of it. Leaving them visible
 * is not just clutter - it invites a client to "upgrade" a product they did not
 * buy, under a logo that is not the one that invoiced them.
 *
 * The account tabs NOT listed here stay on every host on purpose: profile,
 * security, notifications, preferences and privacy are about the person's own
 * account and their own data, and privacy in particular carries the GDPR export
 * and deletion, which a portal client is entitled to just as much.
 */
export const HUSLY_ONLY_TABS = new Set<TabKey>(["billing", "usage", "apikeys", "referral"]);

/** What the two functions below need to know about this visit. */
export interface TabContext {
  /** An operator looking at the portal as the customer would see it. */
  preview: boolean;
  /** The operator's client portal rather than Husly's own hub. */
  portalBrand: boolean;
  /** The referral programme answered - `false` also means "not probed yet". */
  referralEnabled: boolean;
}

/**
 * The account tabs to draw, in menu order.
 *
 * A preview counts as the portal brand whatever the host says: the link is
 * minted onto the portal host in production, but a developer opens it on
 * localhost, where `isClientPortalHost()` is opt-in per tab. Making the preview
 * carry the rule itself keeps the two environments showing the same menu.
 */
export function visibleAccountTabs(all: TabKey[], ctx: TabContext): TabKey[] {
  const clientFacing = ctx.preview || ctx.portalBrand;
  return all.filter((key) => {
    if (clientFacing && HUSLY_ONLY_TABS.has(key)) return false;
    if (key === "referral" && !ctx.referralEnabled) return false;
    return true;
  });
}

/**
 * The tab that actually renders.
 *
 * `requested` is the raw `?tab=` value, so it is untrusted: anything that is not
 * a tab this visit may open falls back to the default. Portal tabs arrive
 * already filtered to the modules this contact was granted, which is why they
 * are passed in rather than listed here - the server owns that answer.
 *
 * The fallbacks differ by audience. An ordinary visit lands on the overview,
 * which greets the signed-in person. A preview lands on projects, because that
 * is what the link was minted from: "View project as customer" should open on
 * the project. The overview can still be OPENED in a preview - there it is the
 * portal's front page, built from the client's own lists - it is just not where
 * a preview starts.
 */
export function resolveActiveTab(
  ctx: TabContext & {
    requested: string | null;
    /** Every account tab this shell knows, in menu order. */
    accountTabs: TabKey[];
    /** The portal tabs this contact may open (empty while the summary loads). */
    openPortalTabs: TabKey[];
  },
): TabKey {
  const allowed = [...visibleAccountTabs(ctx.accountTabs, ctx), ...ctx.openPortalTabs];
  const requested = (ctx.requested ?? "") as TabKey;
  if (allowed.includes(requested)) return requested;
  if (!ctx.preview) return "overview";
  // While the summary is in flight there are no portal tabs yet. Answer
  // "projects" anyway rather than falling back to an account tab: the deep link
  // re-resolves as soon as the modules arrive, and a one-frame flash of the
  // wrong screen is exactly what an operator would report as a bug.
  if (ctx.openPortalTabs.length === 0 || ctx.openPortalTabs.includes("projects")) return "projects";
  return ctx.openPortalTabs[0]!;
}
